import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  isStudioThemeId,
  STUDIO_FORMATS,
  STUDIO_THEMES,
  StudioFormat,
  StudioThemeId,
} from 'fym-shared/src/music24/studioThemes';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRootParamList, SMLRouteNames, SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useQueryParam } from '../../../../utils/hooks/useQueryParam';
import type { KitAssetParams } from '../../../../redux/api/fymAPI.types';
import { PendingStudioParams, takePendingStudioParams } from '../draftStore';
import { registerStudioReturnDraft, takeStudioReturnEdits } from '../credits/creditPackReturn';
import { defaultDetails, StudioDetails, StudioSongContext } from './studioRules';

type StudioParams = NonNullable<SMLRootParamList[SMLRouteNames.promoteStudio]>;

function isStudioFormat(value: string): value is StudioFormat {
  return (STUDIO_FORMATS as readonly string[]).includes(value);
}

function orUndefined(value: string): string | undefined {
  return value === '' ? undefined : value;
}

/** The step-3 details of an asset version's input ("New version" on the song page). */
export function detailsFromParams(params: KitAssetParams): StudioDetails {
  const details: StudioDetails = {};
  if (params.moment !== undefined) {
    details.moment = params.moment;
  }
  if (params.lyricLine !== undefined) {
    details.lyricLine = params.lyricLine;
  }
  if (params.windowStartSec !== undefined) {
    details.windowStartSec = params.windowStartSec;
  }
  if (params.lengthSec !== undefined) {
    details.lengthSec = params.lengthSec;
  }
  if (params.shape !== undefined) {
    details.shape = params.shape;
  }
  if (params.style !== undefined) {
    details.style = params.style;
  }
  if (params.waveform !== undefined) {
    details.waveform = params.waveform;
  }
  if (params.trim !== undefined) {
    details.trim = params.trim;
  }
  if (params.lineFrom !== undefined && params.lineTo !== undefined) {
    details.lineFrom = params.lineFrom;
    details.lineTo = params.lineTo;
  }
  // The server returns only an edited text; with none, the field shows the default again.
  if (params.instructions !== undefined) {
    details.instructions = params.instructions;
  }
  return details;
}

/**
 * The Studio draft (design l1–l5, n1–n5). The song, the format and the theme live in the URL
 * (`catalogItemUuid`, `format`, `theme`), so Back, a reload and a return from Stripe keep them.
 * The step-3 details live in memory; a new song or a new theme starts them again from the theme
 * defaults. A theme with no format in the URL takes the format of the theme.
 *
 * "New version" on the song page leaves the input of that version in `draftStore`. The draft
 * takes it once, when the Studio opens: when its song and theme match the URL, step 3 starts with
 * those details. Otherwise the draft drops it.
 *
 * A credit-pack purchase leaves the app for Stripe. The draft registers its step-3 edits
 * (`registerStudioReturnDraft`), the purchase keeps them in the tab, and after the return step 3
 * starts with them again (`takeStudioReturnEdits`), so "Make it" works at once.
 */
export function useStudioDraft(song: StudioSongContext) {
  const router = useSMLRouter();
  const catalogItemUuid = orUndefined(useQueryParam('catalogItemUuid', undefined, ''));
  const rawFormat = useQueryParam('format', undefined, '');
  const rawTheme = useQueryParam('theme', undefined, '');
  const reelUuid = useQueryParam('reel', undefined, '');

  const themeId: StudioThemeId | undefined = isStudioThemeId(rawTheme) ? rawTheme : undefined;
  const format: StudioFormat | undefined =
    themeId === undefined ? (isStudioFormat(rawFormat) ? rawFormat : undefined) : STUDIO_THEMES[themeId].format;
  // Step 2 is the one screen of every asset type (design t2); the format step (l1) is gone. An
  // old `?format=` link opens step 2 at that group.
  const step: 2 | 3 = themeId === undefined ? 2 : 3;

  const [edits, setEdits] = useState<StudioDetails>({});
  // Null until the first run. It survives the second effect run of React strict mode.
  const pending = useRef<PendingStudioParams | undefined | null>(null);
  // The edits back from a credit-pack purchase, with the song and the asset type they belong to.
  // A ref, so the second effect run of React strict mode keeps them.
  const returned = useRef<{ key: string; edits: StudioDetails } | null>(null);
  useEffect(() => {
    if (pending.current === null) {
      pending.current = takePendingStudioParams();
    }
    const input = pending.current;
    if (input !== undefined && input.catalogItemUuid === catalogItemUuid && input.themeId === themeId) {
      setEdits(detailsFromParams(input.params));
      return;
    }
    pending.current = undefined;
    const key = `${catalogItemUuid ?? ''}/${themeId ?? ''}`;
    const restored = takeStudioReturnEdits(catalogItemUuid, themeId);
    if (restored !== null) {
      returned.current = { key, edits: restored };
    }
    if (returned.current !== null && returned.current.key === key) {
      setEdits(returned.current.edits);
      return;
    }
    returned.current = null;
    setEdits({});
  }, [catalogItemUuid, themeId]);

  // The step-3 edits on screen, for the way back after a credit-pack purchase.
  const editsRef = useRef(edits);
  editsRef.current = edits;
  useEffect(
    () =>
      registerStudioReturnDraft(() => {
        if (catalogItemUuid === undefined || themeId === undefined) {
          return null;
        }
        return { catalogItemUuid, themeId, edits: editsRef.current };
      }),
    [catalogItemUuid, themeId]
  );

  const details = useMemo<StudioDetails>(
    () => (themeId === undefined ? {} : { ...defaultDetails(themeId, song), ...edits }),
    [edits, song, themeId]
  );

  const go = useCallback((params: StudioParams) => router.push(SMLRoutes.promoteStudio, params), [router]);

  // "Next up" on a song card (design t1) opens the details of that asset type at once.
  const chooseSong = useCallback(
    (uuid: string, assetTypeId?: StudioThemeId) => {
      const theme = assetTypeId ?? themeId;
      go({ catalogItemUuid: uuid, format: theme === undefined ? undefined : STUDIO_THEMES[theme].format, theme });
    },
    [go, themeId]
  );
  const changeSong = useCallback(() => go({ format, theme: themeId }), [format, go, themeId]);
  const pickTheme = useCallback(
    (next: StudioThemeId) => go({ catalogItemUuid, format: STUDIO_THEMES[next].format, theme: next }),
    [catalogItemUuid, go]
  );
  const back = useCallback(() => go({ catalogItemUuid }), [catalogItemUuid, go]);
  const restart = useCallback(() => go({ catalogItemUuid }), [catalogItemUuid, go]);
  const setDetails = useCallback((patch: StudioDetails) => setEdits((current) => ({ ...current, ...patch })), []);

  return {
    catalogItemUuid,
    format,
    themeId,
    reelUuid,
    step,
    details,
    setDetails,
    chooseSong,
    changeSong,
    pickTheme,
    back,
    restart,
  };
}

export type StudioDraft = ReturnType<typeof useStudioDraft>;
